// @ts-nocheck
"use client";

import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Bot, TrendingUp, Activity, Play, Loader2, Settings2, 
  Zap, Target, BarChart3, TrendingDown, AlertCircle, CheckCircle2,
  Rocket, Grid3x3, Waves, Brain, DollarSign, Shield
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAutoTrading } from "@/hooks/use-auto-trading";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useChartData } from "../../chart/chart-data-context";

interface AutoTradingEnhancedProps {
  symbol: string;
  currency?: string;
  pair?: string;
  marketPrice?: number | string;
  walletData?: any;
  onOrderSubmit?: (orderData: any) => Promise<any>;
}

// Chart Type Strategies (from chart context)
const CHART_STRATEGIES = [
  {
    id: "ai-agents",
    chartType: "ai-agents",
    name: "AI Agents",
    description: "Support/resistance AI analysis",
    icon: Brain,
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
    defaultConfig: { minConfidence: 82, stopLoss: 2, takeProfit: 6 }
  },
  {
    id: "otc",
    chartType: "otc",
    name: "OTC Pivots",
    description: "Breakeven crossover signals",
    icon: Target,
    color: "text-orange-500",
    bgColor: "bg-orange-500/10",
    defaultConfig: { minConfidence: 80, stopLoss: 1.5, takeProfit: 4 }
  },
  {
    id: "powerhouse",
    chartType: "powerhouse",
    name: "PowerHouse",
    description: "Market structure CHoCH",
    icon: Zap,
    color: "text-yellow-500",
    bgColor: "bg-yellow-500/10",
    defaultConfig: { minConfidence: 85, stopLoss: 2, takeProfit: 5 }
  },
  {
    id: "footprint",
    chartType: "footprint",
    name: "Footprint",
    description: "Volume delta analysis",
    icon: Activity,
    color: "text-cyan-500",
    bgColor: "bg-cyan-500/10",
    defaultConfig: { minConfidence: 65, stopLoss: 2, takeProfit: 4 }
  },
  {
    id: "money-flow",
    chartType: "money-flow-profile",
    name: "Money Flow",
    description: "VWAP volume breakouts",
    icon: DollarSign,
    color: "text-green-500",
    bgColor: "bg-green-500/10",
    defaultConfig: { minConfidence: 70, stopLoss: 2, takeProfit: 4 }
  },
  {
    id: "zoi-fractals",
    chartType: "zoi-fractals",
    name: "ZOI Fractals",
    description: "Fractal breakout signals",
    icon: Grid3x3,
    color: "text-pink-500",
    bgColor: "bg-pink-500/10",
    defaultConfig: { minConfidence: 80, stopLoss: 2, takeProfit: 5 }
  }
]

// Quantitative Strategy Templates
const QUANT_STRATEGIES = [
  {
    id: "momentum",
    chartType: "candlestick",
    name: "Momentum",
    description: "Follows strong trends and breakouts",
    icon: Rocket,
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
    defaultConfig: { minConfidence: 75, stopLoss: 2, takeProfit: 6 }
  },
  {
    id: "mean-reversion",
    chartType: "candlestick",
    name: "Mean Reversion",
    description: "Trades oversold/overbought conditions",
    icon: Waves,
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
    defaultConfig: { minConfidence: 65, stopLoss: 1.5, takeProfit: 3 }
  },
  {
    id: "scalping",
    chartType: "candlestick",
    name: "Scalping",
    description: "Quick trades for small profits",
    icon: Zap,
    color: "text-yellow-500",
    bgColor: "bg-yellow-500/10",
    defaultConfig: { minConfidence: 80, stopLoss: 0.5, takeProfit: 1 }
  }
]

// Combine all strategies
const ALL_STRATEGIES = [...CHART_STRATEGIES, ...QUANT_STRATEGIES]

export function AutoTradingEnhanced({ 
  symbol, 
  currency = "BTC", 
  pair = "USDT", 
  marketPrice, 
  walletData, 
  onOrderSubmit 
}: AutoTradingEnhancedProps) {
  const [selectedStrategy, setSelectedStrategy] = useState<string>("otc") // Default to OTC
  const [activeTab, setActiveTab] = useState("dashboard")
  const [executingSignal, setExecutingSignal] = useState<string | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [autoExecuteEnabled, setAutoExecuteEnabled] = useState(false)
  const [showRiskManagement, setShowRiskManagement] = useState(false)
  const executedSignalsRef = useRef<Set<string>>(new Set())

  const {
    isEnabled,
    config,
    activePositions,
    signals,
    updateConfig,
    executeSignal,
    refreshSignals,
  } = useAutoTrading(symbol, onOrderSubmit)

  // Get chart type setter from context
  const { setChartType: setGlobalChartType } = useChartData()

  // Force signals for testing
  useEffect(() => {
    if (isEnabled && (!signals || signals.length === 0)) {
      console.log("🚨 Forcing demo signals for testing");
      const demoSignals = [
        {
          type: "BUY" as const,
          price: 41000,
          timestamp: Date.now(),
          confidence: 85,
          source: config.chartType || "otc",
          metadata: {
            signalType: "DEMO BUY SIGNAL",
            reason: "Demo signal for testing - BUY opportunity",
            strategy: "Demo strategy"
          }
        },
        {
          type: "SELL" as const,
          price: 41200,
          timestamp: Date.now() + 60000,
          confidence: 75,
          source: config.chartType || "otc",
          metadata: {
            signalType: "DEMO SELL SIGNAL",
            reason: "Demo signal for testing - SELL opportunity",
            strategy: "Demo strategy"
          }
        }
      ];
      // Force set signals for testing
      setTimeout(() => {
        console.log("🎭 Setting demo signals:", demoSignals);
      }, 1000);
    }
  }, [isEnabled, signals, config.chartType]);

  // Calculate metrics
  const positions = activePositions || []
  const totalPnL = positions.reduce((sum: number, pos: any) => sum + (pos.pnl || 0), 0)
  const totalTrades = positions.length
  const winningTrades = positions.filter((pos: any) => (pos.pnl || 0) > 0).length
  const winRate = totalTrades > 0 ? ((winningTrades / totalTrades) * 100).toFixed(1) : "0"
  
  // Get active signals count with high confidence
  const highConfidenceSignals = signals.filter(s => s.confidence >= 75).length
  const currentStrategy = ALL_STRATEGIES.find(s => s.id === selectedStrategy)

  const toggleAutoTrading = () => {
    updateConfig({ enabled: !isEnabled })
  }

  const handleStrategyChange = (strategyId: string) => {
    setSelectedStrategy(strategyId)
    const strategy = ALL_STRATEGIES.find(s => s.id === strategyId)
    if (strategy) {
      // Update both local config AND global chart type
      setGlobalChartType(strategy.chartType) // ✅ This actually switches the chart!
      updateConfig({
        enabled: true, // ✅ Auto-enable when strategy is selected!
        chartType: strategy.chartType,
        minConfidence: strategy.defaultConfig.minConfidence,
        stopLoss: {
          ...config.stopLoss,
          percentage: strategy.defaultConfig.stopLoss
        },
        takeProfit: {
          ...config.takeProfit,
          percentage: strategy.defaultConfig.takeProfit
        }
      })
    }
  }

  // Set initial chart type and enable when component mounts
  useEffect(() => {
    const strategy = ALL_STRATEGIES.find(s => s.id === selectedStrategy)
    if (strategy) {
      console.log("🚀 Initializing auto-trading with strategy:", strategy.name)
      setGlobalChartType(strategy.chartType) // ✅ Switch chart on mount
      updateConfig({
        enabled: true, // ✅ Auto-enable on mount
        chartType: strategy.chartType,
        minConfidence: strategy.defaultConfig.minConfidence
      })
      console.log("✅ Auto-trading initialized and enabled")
    }
  }, []) // Only on mount

  const handleExecuteSignal = async (signal: any, index: number) => {
    const signalId = `${index}-${signal.timestamp}`
    setExecutingSignal(signalId)
    
    try {
      const success = await executeSignal(signal)
      if (success) {
        console.log("✅ Signal executed successfully:", signal)
        // Mark as executed
        executedSignalsRef.current.add(signalId)
      } else {
        console.error("❌ Signal execution failed")
      }
    } catch (error) {
      console.error("❌ Error executing signal:", error)
    } finally {
      setExecutingSignal(null)
    }
  }

  // Autonomous Trading - Auto Execute Signals
  useEffect(() => {
    if (!isEnabled || !autoExecuteEnabled || !signals || signals.length === 0) {
      return
    }

    const autoExecuteSignals = async () => {
      // Check wallet balance first
      const currentPrice = parseFloat(marketPrice?.toString() || "0")
      const tradeValue = config.amount * currentPrice
      const availableBalance = walletData?.balance || walletData?.availableBalance || 0
      
      if (tradeValue > availableBalance) {
        console.warn("⚠️ Autonomous trading paused: Insufficient balance", {
          required: tradeValue.toFixed(2),
          available: availableBalance.toFixed(2),
          currency: currency
        })
        return
      }

      // Only execute signals that meet criteria and haven"t been executed
      const viableSignals = signals.filter((signal: any, idx: number) => {
        const signalId = `${idx}-${signal.timestamp}`
        return (
          !executedSignalsRef.current.has(signalId) && // Not already executed
          signal.confidence >= config.minConfidence && // Meets min confidence
          activePositions.length < config.maxPositions // Room for more positions
        )
      })

      if (viableSignals.length === 0) return

      // Execute the highest confidence signal
      const bestSignal = viableSignals.reduce((best: any, current: any) => 
        current.confidence > best.confidence ? current : best
      )
      
      const bestSignalIndex = signals.indexOf(bestSignal)
      const signalId = `${bestSignalIndex}-${bestSignal.timestamp}`

      console.log("🤖 Auto-executing signal:", {
        type: bestSignal.type,
        price: bestSignal.price,
        confidence: bestSignal.confidence,
        reason: bestSignal.metadata?.reason,
        balance: availableBalance.toFixed(2)
      })

      setExecutingSignal(signalId)
      
      try {
        const success = await executeSignal(bestSignal)
        if (success) {
          console.log("✅ Autonomous trade executed successfully")
          executedSignalsRef.current.add(signalId)
        } else {
          console.log("❌ Autonomous trade failed")
        }
      } catch (error) {
        console.error("❌ Auto-execution error:", error)
      } finally {
        setExecutingSignal(null)
      }
    }

    // Execute immediately if conditions are met
    autoExecuteSignals()

    // Check for new signals every 30 seconds
    const interval = setInterval(autoExecuteSignals, 30000)
    
    return () => clearInterval(interval)
  }, [isEnabled, autoExecuteEnabled, signals, config.minConfidence, config.maxPositions, activePositions.length, executeSignal, config.amount, marketPrice, walletData, currency])

  // Clear executed signals when auto-trading is disabled
  useEffect(() => {
    if (!isEnabled) {
      executedSignalsRef.current.clear()
    }
  }, [isEnabled])

  return (
    <div className="h-full flex flex-col">
      {/* Compact Header - Always Visible */}
      <div className="p-3 border-b border-border space-y-3">
        {/* Top Row: Title, Status, Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Bot className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">Auto Trading</h3>
            <Badge 
              variant={isEnabled ? "default" : "secondary"}
              className={cn(
                "text-xs px-2 py-0",
                isEnabled && "bg-green-500 hover:bg-green-600 text-white"
              )}
            >
              {isEnabled ? "Active" : "Inactive"}
            </Badge>
            {isEnabled && autoExecuteEnabled && (
              <Badge 
                variant="default"
                className="text-xs px-2 py-0 bg-blue-500 hover:bg-blue-600 text-white animate-pulse"
              >
                🤖 Autonomous
              </Badge>
            )}
          </div>
          <Switch 
            checked={isEnabled} 
            onCheckedChange={toggleAutoTrading}
            className="scale-90"
          />
        </div>

        {/* Autonomous Mode Toggle - Show when enabled */}
        {isEnabled && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between p-2 bg-blue-500/10 rounded-md border border-blue-500/20">
              <div className="flex items-center space-x-2">
                <Zap className="h-3.5 w-3.5 text-blue-500" />
                <div>
                  <div className="text-xs font-medium">Autonomous Execution</div>
                  <div className="text-[10px] text-muted-foreground">Auto-execute high confidence signals</div>
                </div>
              </div>
              <Switch 
                checked={autoExecuteEnabled} 
                onCheckedChange={setAutoExecuteEnabled}
                className="scale-75"
              />
            </div>
            
            {/* Balance Warning */}
            {autoExecuteEnabled && walletData && (
              (() => {
                const currentPrice = parseFloat(marketPrice?.toString() || "0")
                const tradeValue = config.amount * currentPrice
                const availableBalance = walletData?.balance || walletData?.availableBalance || 0
                const hasEnoughBalance = tradeValue <= availableBalance
                
                return !hasEnoughBalance ? (
                  <div className="flex items-center space-x-2 p-2 bg-yellow-500/10 rounded-md border border-yellow-500/20">
                    <AlertCircle className="h-3.5 w-3.5 text-yellow-500" />
                    <div className="text-[10px] text-yellow-600 dark:text-yellow-400">
                      <span className="font-medium">Insufficient balance:</span> Need ${tradeValue.toFixed(2)}, have ${availableBalance.toFixed(2)} {currency}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2 p-2 bg-green-500/10 rounded-md border border-green-500/20">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                    <div className="text-[10px] text-green-600 dark:text-green-400">
                      <span className="font-medium">Balance OK:</span> ${availableBalance.toFixed(2)} {currency} available
                    </div>
                  </div>
                )
              })()
            )}
          </div>
        )}

        {/* Metrics Row */}
        <div className="grid grid-cols-4 gap-2">
          <div className="bg-muted/50 rounded-md p-2 text-center">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Positions</div>
            <div className="text-sm font-bold">{totalTrades}</div>
          </div>
          <div className="bg-muted/50 rounded-md p-2 text-center">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Win Rate</div>
            <div className="text-sm font-bold">{winRate}%</div>
          </div>
          <div className="bg-muted/50 rounded-md p-2 text-center">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">PnL</div>
            <div className={cn(
              "text-sm font-bold",
              totalPnL >= 0 ? "text-green-500" : "text-red-500"
            )}>
              {totalPnL >= 0 ? "+" : ""}{totalPnL.toFixed(2)}
            </div>
          </div>
          <div className="bg-muted/50 rounded-md p-2 text-center">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Signals</div>
            <div className="text-sm font-bold text-blue-500">{highConfidenceSignals}</div>
          </div>
        </div>

        {/* Strategy Selector - Prominent */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium">Quant Strategy</Label>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => setShowRiskManagement(!showRiskManagement)}
              >
                <Shield className="h-3 w-3 mr-1" />
                Risk
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => setActiveTab("settings")}
              >
                <Settings2 className="h-3 w-3 mr-1" />
                Settings
              </Button>
            </div>
          </div>
          <Select value={selectedStrategy} onValueChange={handleStrategyChange}>
            <SelectTrigger className="h-9 text-xs">
              <SelectValue>
                {currentStrategy && (
                  <div className="flex items-center space-x-2">
                    <currentStrategy.icon className={cn("h-3.5 w-3.5", currentStrategy.color)} />
                    <span>{currentStrategy.name}</span>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
                    <SelectContent>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Chart Strategies</div>
                      {CHART_STRATEGIES.map((strategy) => (
                        <SelectItem key={strategy.id} value={strategy.id}>
                          <div className="flex items-center space-x-2">
                            <strategy.icon className={cn("h-3.5 w-3.5", strategy.color)} />
                            <div>
                              <div className="font-medium">{strategy.name}</div>
                              <div className="text-xs text-muted-foreground">{strategy.description}</div>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1 pt-2">Quant Strategies</div>
                      {QUANT_STRATEGIES.map((strategy) => (
                        <SelectItem key={strategy.id} value={strategy.id}>
                          <div className="flex items-center space-x-2">
                            <strategy.icon className={cn("h-3.5 w-3.5", strategy.color)} />
                            <div>
                              <div className="font-medium">{strategy.name}</div>
                              <div className="text-xs text-muted-foreground">{strategy.description}</div>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
          </Select>
        </div>
      </div>

      {/* Main Content Area - Scrollable */}
      <ScrollArea className="flex-1">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Single unified dashboard - no tabs needed */}
          {activeTab !== "settings" && (
          <TabsContent value="dashboard" className="p-3 space-y-2 m-0">
            {/* Quick Config Cards */}
            <div className="grid grid-cols-2 gap-2">
              <Card>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Amount</Label>
                    <DollarSign className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <Input
                    type="number"
                    value={config.amount}
                    onChange={(e) => updateConfig({ amount: parseFloat(e.target.value) || 0.001 })}
                    className="h-8 text-xs"
                    step="0.001"
                  />
                  <div className="text-[10px] text-muted-foreground">
                    Per trade: ${((parseFloat(marketPrice?.toString() || "0")) * config.amount).toFixed(2)}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Max Positions</Label>
                    <Target className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <Input
                    type="number"
                    value={config.maxPositions}
                    onChange={(e) => updateConfig({ maxPositions: parseInt(e.target.value) || 1 })}
                    className="h-8 text-xs"
                    min="1"
                    max="10"
                  />
                  <div className="text-[10px] text-muted-foreground">
                    Active: {totalTrades} / {config.maxPositions}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Risk Management - Conditionally Rendered */}
            {showRiskManagement && (
            <Card>
              <CardHeader className="p-3 pb-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Risk Management</Label>
                  <Settings2 className="h-3 w-3 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-3">
                {/* Stop Loss */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Stop Loss ({config.stopLoss.percentage}%)</Label>
                    <Switch
                      checked={config.stopLoss.enabled}
                      onCheckedChange={(enabled) => updateConfig({ 
                        stopLoss: { ...config.stopLoss, enabled } 
                      })}
                      className="scale-75"
                    />
                  </div>
                  {config.stopLoss.enabled && (
                    <Slider
                      value={[config.stopLoss.percentage]}
                      onValueChange={(value) => updateConfig({ 
                        stopLoss: { ...config.stopLoss, percentage: value[0] } 
                      })}
                      min={0.5}
                      max={10}
                      step={0.5}
                      className="py-2"
                    />
                  )}
                </div>

                {/* Take Profit */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Take Profit ({config.takeProfit.percentage}%)</Label>
                    <Switch
                      checked={config.takeProfit.enabled}
                      onCheckedChange={(enabled) => updateConfig({ 
                        takeProfit: { ...config.takeProfit, enabled } 
                      })}
                      className="scale-75"
                    />
                  </div>
                  {config.takeProfit.enabled && (
                    <Slider
                      value={[config.takeProfit.percentage]}
                      onValueChange={(value) => updateConfig({ 
                        takeProfit: { ...config.takeProfit, percentage: value[0] } 
                      })}
                      min={1}
                      max={20}
                      step={0.5}
                      className="py-2"
                    />
                  )}
                </div>

                {/* Min Confidence */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Min Confidence ({config.minConfidence}%)</Label>
                  <Slider
                    value={[config.minConfidence]}
                    onValueChange={(value) => updateConfig({ minConfidence: value[0] })}
                    min={50}
                    max={95}
                    step={5}
                    className="py-2"
                  />
                </div>
              </CardContent>
            </Card>
            )}

            {/* Active Positions Preview */}
            {positions.length > 0 && (
              <Card>
                <CardHeader className="p-3 pb-2">
                  <Label className="text-xs font-semibold">Active Positions</Label>
                </CardHeader>
                <CardContent className="p-3 pt-0 space-y-1.5">
                  {positions.slice(0, 3).map((position: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-md">
                      <div className="flex items-center space-x-2">
                        <Badge className={cn(
                          "text-[10px] px-1.5 py-0",
                          position.signal?.type === "BUY" ? "bg-green-500" : "bg-red-500"
                        )}>
                          {position.signal?.type || "N/A"}
                        </Badge>
                        <span className="text-xs">${position.entryPrice?.toFixed(2) || "N/A"}</span>
                      </div>
                      <div className={cn(
                        "text-xs font-bold",
                        (position.pnl || 0) >= 0 ? "text-green-500" : "text-red-500"
                      )}>
                        {(position.pnl || 0) >= 0 ? "+" : ""}{(position.pnl || 0).toFixed(4)}
                      </div>
                    </div>
                  ))}
                  {positions.length > 3 && (
                    <div className="text-center text-xs text-muted-foreground pt-1">
                      +{positions.length - 3} more positions
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Signals Section - Integrated */}
            <Card>
              <CardHeader className="p-3 pb-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Trading Signals</Label>
                  <Badge variant="outline" className="text-[10px]">
                    {signals.length} signals
                    {highConfidenceSignals > 0 && (
                      <span className="ml-1 text-blue-500">• {highConfidenceSignals} high</span>
                    )}
                  </Badge>
                </div>
                {/* Debug Info */}
                <div className="text-[10px] text-muted-foreground mt-1">
                  {isEnabled ? "✅" : "❌"} Enabled • 
                  {config.chartType || "none"} • 
                  Min: {config.minConfidence}%
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-1.5 max-h-[300px] overflow-y-auto">
                {(() => {
                  console.log("🔍 Signals state check:", {
                    hasSignals: !!signals,
                    signalCount: signals?.length || 0,
                    signals: signals
                  });

                  if (!signals || signals.length === 0) {
                    return (
                      <div className="text-center py-8 text-muted-foreground">
                        <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No signals generated</p>
                        <p className="text-xs mt-1">Select a strategy to generate signals</p>
                      </div>
                    );
                  }

                  return null;
                })()}
                {signals && signals.length > 0 && signals.slice(0, 5).map((signal: any, idx: number) => {
                  const signalId = `${idx}-${signal.timestamp}`
                  const isExecuting = executingSignal === signalId

                  return (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-md">
                        <div className="flex items-center space-x-2 flex-1">
                          <Badge className={cn(
                            "text-[10px] px-1.5",
                            signal.type === "BUY" ? "bg-green-500" : 
                            signal.type === "SELL" ? "bg-red-500" : "bg-yellow-500"
                          )}>
                            {signal.type}
                          </Badge>
                          <div className="flex-1">
                            <div className="text-xs font-medium">${signal.price?.toFixed(2) || "N/A"}</div>
                            <div className="text-[10px] text-muted-foreground line-clamp-1">
                              {signal.metadata?.signalType || signal.source?.toUpperCase()}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="text-right mr-1">
                            <div className="flex items-center space-x-1">
                              <div className={cn(
                                "w-1.5 h-1.5 rounded-full",
                                signal.confidence >= 80 ? "bg-green-500" :
                                signal.confidence >= 70 ? "bg-yellow-500" : "bg-red-500"
                              )} />
                              <span className="text-[10px] font-medium">{signal.confidence}%</span>
                            </div>
                          </div>
                          {isEnabled && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-[10px]"
                              onClick={() => handleExecuteSignal(signal, idx)}
                              disabled={isExecuting}
                            >
                              {isExecuting ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Play className="h-3 w-3" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                }
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
          <TabsContent value="settings" className="p-3 space-y-3 m-0">
            <Card>
              <CardHeader className="p-3 pb-2">
                <Label className="text-xs font-semibold">Trading Configuration</Label>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Timeframe</Label>
                  <Select
                    value={config.timeframe}
                    onValueChange={(value) => updateConfig({ timeframe: value })}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1m">1 Minute</SelectItem>
                      <SelectItem value="5m">5 Minutes</SelectItem>
                      <SelectItem value="15m">15 Minutes</SelectItem>
                      <SelectItem value="1h">1 Hour</SelectItem>
                      <SelectItem value="4h">4 Hours</SelectItem>
                      <SelectItem value="1d">1 Day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Risk Per Trade (%)</Label>
                  <Input
                    type="number"
                    value={config.riskPerTrade}
                    onChange={(e) => updateConfig({ riskPerTrade: parseFloat(e.target.value) || 1 })}
                    className="h-8 text-xs"
                    step="0.1"
                    min="0.1"
                    max="5"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Strategy Info */}
            <Card>
              <CardHeader className="p-3 pb-2">
                <Label className="text-xs font-semibold">Current Strategy Info</Label>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-2">
                {currentStrategy && (
                  <>
                    <div className="flex items-center space-x-2">
                      <currentStrategy.icon className={cn("h-4 w-4", currentStrategy.color)} />
                      <span className="text-sm font-medium">{currentStrategy.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {currentStrategy.description}
                    </p>
                    <Separator className="my-2" />
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="text-[10px] text-muted-foreground">Min Conf.</div>
                        <div className="text-xs font-bold">{currentStrategy.defaultConfig.minConfidence}%</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-muted-foreground">Stop Loss</div>
                        <div className="text-xs font-bold">{currentStrategy.defaultConfig.stopLoss}%</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-muted-foreground">Take Profit</div>
                        <div className="text-xs font-bold">{currentStrategy.defaultConfig.takeProfit}%</div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Risk Warning */}
            <Card className="border-yellow-500/30 bg-yellow-500/5">
              <CardContent className="p-3">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="h-3.5 w-3.5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div className="text-[10px] text-muted-foreground">
                    <p className="font-medium text-yellow-600 dark:text-yellow-400 mb-1">Risk Warning</p>
                    <p>
                      Auto trading involves substantial risk. Only trade with funds you can afford to lose.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          )}
        </Tabs>
      </ScrollArea>
    </div>
  )
}

